import React, { useState, useEffect } from 'react'
import { X, Copy, Check, Play, Sparkles, Volume2, VolumeX, Music } from 'lucide-react'
import { usePlayerPreferences, SupportedLanguage } from '../../providers/PlayerPreferencesProvider'
import { preloadSounds } from '../../utils/audio'
import { useTourGuide } from '../../providers/TourGuideProvider'
import { useAccount, useChainId } from 'wagmi'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import { useMiniKit } from '@coinbase/onchainkit/minikit'
import { logger } from '../../utils/logger'

// AGC: Game version constant (must match MainMenu.tsx)
const GAME_VERSION = '6.10.6 (Base App 6.10.6)'

// AGC: User profile type from Farcaster Mini App SDK
interface FarcasterUser {
  fid: number
  username?: string
  displayName?: string
  pfpUrl?: string
  bio?: string
}

interface PlayerSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

const languageOptions: Array<{ value: SupportedLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
]

export const PlayerSettingsModal: React.FC<PlayerSettingsModalProps> = ({ isOpen, onClose }) => {
  const { preferences, updatePreferences } = usePlayerPreferences()
  const { openTourSelection } = useTourGuide()
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { isBaseAppContext } = useFeatureFlags()
  const [copied, setCopied] = useState(false)
  
  // AGC: Use MiniKit hook to get user profile data in Base App
  const miniKit = isBaseAppContext ? useMiniKit() : null
  const miniKitContext = miniKit?.context

  // AGC: Extract Farcaster user data from MiniKit context
  const farcasterUser: FarcasterUser | null = React.useMemo(() => {
    if (!isBaseAppContext) {
      logger.debug('Not in Base App context - profile not available')
      return null
    }

    if (!miniKit) {
      logger.debug('MiniKit hook not available')
      return null
    }

    if (!miniKitContext) {
      logger.debug('MiniKit context not available (likely testing on localhost, not in actual Base App)')
      return null
    }

    if (!miniKitContext.user) {
      logger.debug('MiniKit context.user not available:', {
        hasContext: !!miniKitContext,
        contextKeys: miniKitContext ? Object.keys(miniKitContext) : [],
        isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      })
      return null
    }

    const user = miniKitContext.user
    const userData: FarcasterUser = {
      fid: user.fid || 0,
      username: user.username,
      displayName: user.displayName,
      pfpUrl: user.pfpUrl,
      bio: user.bio
    }

    // AGC: Only return user data if we have at least a FID
    if (userData.fid > 0) {
      logger.info('✅ Loaded Farcaster user profile from MiniKit:', {
        fid: userData.fid,
        username: userData.username,
        displayName: userData.displayName,
        hasPfp: !!userData.pfpUrl
      })
      return userData
    }

    logger.debug('User data found but FID is 0 or missing')
    return null
  }, [isBaseAppContext, miniKit, miniKitContext])

  if (!isOpen) {
    return null
  }

  const formatAddress = (addr: string) => {
    if (!addr) return 'Not Connected'
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  const getNetworkName = (id: number) => {
    if (id === 8453) return 'Base Mainnet'
    if (id === 84532) return 'Base Sepolia'
    return `Chain ${id}`
  }

  const handleCopyAddress = async () => {
    if (!address) return
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
        logger.error('Failed to copy address:', err)
    }
  }

  const handleModalToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    updatePreferences({ showTurnBreakdownModal: event.target.checked })
  }

  // AGC: Tour welcome modal toggle (v6.9.0)
  const handleTourWelcomeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    updatePreferences({ showTourWelcomeModal: event.target.checked })
  }

  // AGC: Replay tour - close settings and open tour selection (v6.9.0)
  const handleReplayTour = () => {
    onClose()
    // AGC: Small delay to allow modal to close
    setTimeout(() => {
      openTourSelection()
    }, 300)
  }

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    updatePreferences({ language: event.target.value as SupportedLanguage })
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-slate-900 border border-blue-500/40 rounded-2xl shadow-2xl p-6 space-y-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
          aria-label="Close settings"
        >
          <X className="w-4 h-4" />
        </button>

        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Player Settings</h2>
          <p className="text-sm text-slate-300">Customize your Origami Battles experience.</p>
        </div>

        <div className="space-y-4">
          {/* AGC: Wallet Information Section */}
            <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-white">Wallet Information</h3>
            
            {/* AGC: Farcaster Profile (Base App) - Integrated into Wallet Information */}
            {isBaseAppContext && farcasterUser && (
              <div className="flex items-center gap-3 pb-3 border-b border-slate-700">
                {farcasterUser.pfpUrl && (
                  <img
                    src={farcasterUser.pfpUrl}
                    alt="Profile"
                    className="w-10 h-10 rounded-full border-2 border-blue-500/50 object-cover"
                    onError={(e) => {
                      // AGC: Hide image if it fails to load
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {farcasterUser.displayName || farcasterUser.username || 'Unknown User'}
                  </div>
                  {farcasterUser.username && (
                    <div className="text-xs text-slate-400 truncate">
                      @{farcasterUser.username}
                    </div>
                  )}
              </div>
            </div>
          )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Status</span>
                <span className={`text-xs font-semibold ${isConnected ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              {address && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Address</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-white">{formatAddress(address)}</span>
                      <button
                        onClick={handleCopyAddress}
                        className="p-1 rounded hover:bg-slate-700 transition-colors"
                        title="Copy address"
                      >
                        {copied ? (
                          <Check className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <Copy className="w-3 h-3 text-slate-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Network</span>
                    <span className="text-xs font-semibold text-white">{getNetworkName(chainId)}</span>
                  </div>
                  {isBaseAppContext && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Environment</span>
                      <span className="text-xs font-semibold text-blue-400">Base App</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* AGC: Turn Breakdown Modal - 2 lines */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <p className="text-white font-semibold text-sm">Turn Breakdown Result</p>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-500 bg-slate-800"
                  checked={preferences.showTurnBreakdownModal}
                  onChange={handleModalToggle}
                />
                <span className="text-slate-200 text-sm">Enable</span>
              </label>
            </div>
            <p className="text-xs text-slate-400 pl-0">Very detailed explanation of turn results</p>
          </div>

          {/* AGC: Interactive Tour Settings - 2 lines */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <p className="text-white font-semibold text-sm">Welcome Tour on Startup</p>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-500 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                  checked={preferences.showTourWelcomeModal}
                  onChange={handleTourWelcomeToggle}
                />
                <span className="text-slate-200 text-sm">Enable</span>
              </label>
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={handleReplayTour}
                className="flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white text-xs font-semibold transition-all shadow-lg shadow-purple-500/20"
              >
                <Play className="w-3 h-3" />
                Replay Tour
              </button>
            </div>
          </div>

          {/* AGC: Sound Settings - v6.10.1 */}
          <div className="space-y-3">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              Sound Settings
            </p>
            
            {/* AGC: Sound Effects Toggle */}
            <div className="flex items-center justify-between gap-4 pl-6">
              <div className="flex items-center gap-2">
                <Volume2 className="w-3 h-3 text-slate-400" />
                <span className="text-slate-300 text-sm">Sound Effects</span>
              </div>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-500 bg-slate-800 text-green-500 focus:ring-green-500"
                  checked={preferences.soundEffectsEnabled !== false}
                  onChange={(e) => {
                    updatePreferences({ soundEffectsEnabled: e.target.checked })
                    if (e.target.checked) {
                      preloadSounds() // AGC: Pre-load sounds when enabled
                    }
                  }}
                />
                <span className="text-slate-200 text-sm">Enable</span>
              </label>
            </div>
            
            {/* AGC: Music Toggle */}
            <div className="flex items-center justify-between gap-4 pl-6">
              <div className="flex items-center gap-2">
                <Music className="w-3 h-3 text-slate-400" />
                <span className="text-slate-300 text-sm">Background Music</span>
              </div>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-500 bg-slate-800 text-purple-500 focus:ring-purple-500"
                  checked={preferences.musicEnabled !== false}
                  onChange={(e) => updatePreferences({ musicEnabled: e.target.checked })}
                />
                <span className="text-slate-200 text-sm">Enable</span>
              </label>
            </div>
            
            {/* AGC: Reset music popup button */}
            <div className="flex items-center justify-end pl-6">
              <button
                onClick={() => {
                  localStorage.removeItem('origami-music-popup-dismissed-never')
                  localStorage.removeItem('origami-music-popup-dismissed-later')
                  alert('Music popup will show again on next page load!')
                }}
                className="flex items-center justify-center gap-1.5 py-1 px-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold transition-all"
              >
                <Music className="w-3 h-3" />
                Reset Music Popup
              </button>
            </div>
          </div>

          {/* AGC: Language - 2 lines */}
          <div className="space-y-1">
            <p className="text-white font-semibold text-sm">Language (coming soon)</p>
            <select
              value={preferences.language}
              onChange={handleLanguageChange}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* AGC: Version display */}
        <div className="pt-4 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">
            Version {GAME_VERSION}
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

